import React, { useEffect, useMemo, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { motion } from "framer-motion";
import html2canvas from "html2canvas";
import profileHighlight from "../assests/upi-profile-highlight.jpeg";
import { fetchGlobalUpiVerifyRecords } from "../services/upiVerifySource";
import API_BASE from "../config/apiBase";

const normalizeUpiId = (value = "") => value.trim().toLowerCase().replace(/\s+/g, "");

const isValidUpiId = (value) =>
  /^[a-z0-9._-]{2,256}@[a-z0-9._-]{2,64}$/i.test(normalizeUpiId(value));

const encodeValue = (value) => encodeURIComponent((value || "").trim());

const buildUpiPayload = ({ upiId, payeeName, amount }) => {
  const params = [
    `pa=${encodeValue(upiId)}`,
    payeeName ? `pn=${encodeValue(payeeName)}` : "",
    amount ? `am=${encodeValue(amount)}` : "",
    "cu=INR",
  ].filter(Boolean);

  return `upi://pay?${params.join("&")}`;
};

const parseUpiPayload = (value = "") => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return { upiId: "", payeeName: "", amount: "" };
  }

  if (!trimmedValue.toLowerCase().startsWith("upi://")) {
    return { upiId: normalizeUpiId(trimmedValue), payeeName: "", amount: "" };
  }

  try {
    const url = new URL(trimmedValue);
    return {
      upiId: normalizeUpiId(url.searchParams.get("pa") || ""),
      payeeName: (url.searchParams.get("pn") || "").trim(),
      amount: (url.searchParams.get("am") || "").trim(),
    };
  } catch (error) {
    console.error("Unable to parse scanned UPI payload:", error);
    return { upiId: "", payeeName: "", amount: "" };
  }
};


const getBarcodeDetector = () => {
  if (typeof window === "undefined" || !("BarcodeDetector" in window)) {
    return null;
  }

  try {
    return new window.BarcodeDetector({ formats: ["qr_code"] });
  } catch (error) {
    console.error("BarcodeDetector is not available:", error);
    return null;
  }
};

const UpiVerify = () => {
  const [form, setForm] = useState({
    upiId: "",
    payeeName: "",
    amount: "",
  });
  const [records, setRecords] = useState([]);
  const [hasChecked, setHasChecked] = useState(false);
  const [scanMessage, setScanMessage] = useState("");
  const [scanError, setScanError] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [lookupMessage, setLookupMessage] = useState("");
  const [liveLookupLoading, setLiveLookupLoading] = useState(false);

  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const frameRef = useRef(null);
  const shareCardRef = useRef(null);
  const shareImageRef = useRef(null);

  useEffect(() => {
    const fetchUpiRecords = async () => {
      try {
        const data = await fetchGlobalUpiVerifyRecords();
        setRecords(data);
        setLookupMessage(
          data.length
            ? `Loaded ${data.length} global UPI record(s).`
            : "No global UPI records found in the configured source."
        );
      } catch (error) {
        console.error("Error fetching UPI records for verification:", error);
        setLookupMessage("Unable to load global UPI records right now.");
      } finally {
      }
    };

    fetchUpiRecords();
  }, []);

  useEffect(() => {
    detectorRef.current = getBarcodeDetector();
  }, []);

  useEffect(() => {
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const normalizedInput = useMemo(() => normalizeUpiId(form.upiId), [form.upiId]);

  const matchedRecords = useMemo(() => {
    if (!normalizedInput) {
      return [];
    }

    return records.filter((record) => record.normalizedUpi === normalizedInput);
  }, [normalizedInput, records]);

  const verifiedRecord = matchedRecords[0] || null;

  const qrPayload = useMemo(() => {
    if (!isValidUpiId(form.upiId)) {
      return "";
    }

    return buildUpiPayload(form);
  }, [form]);

  const qrImageUrl = useMemo(() => {
    if (!qrPayload) {
      return "";
    }

    return `https://api.qrserver.com/v1/create-qr-code/?size=700x700&data=${encodeURIComponent(
      qrPayload
    )}`;
  }, [qrPayload]);

  const stopScanner = () => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsScannerOpen(false);
  };

  const setVerifiedUpi = (upiData) => {
    const normalized = normalizeUpiId(upiData.upiId || upiData);
    const matchedRecord = records.find((record) => record.normalizedUpi === normalized);

    setForm((prev) => ({
      ...prev,
      upiId: normalized,
      payeeName: matchedRecord?.name || upiData.payeeName || "",
      amount: upiData.amount || prev.amount,
    }));
    setHasChecked(Boolean(normalized));
  };

  const fetchLiveUpiName = async (upiId) => {
    const response = await fetch(`${API_BASE}/api/upi/verify-name`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ upiId }),
    });

    const payload = await response.json().catch(() => ({}));
    return { response, payload };
  };

  const handleDetectedPayload = (payload) => {
    const parsedPayload = parseUpiPayload(payload);

    if (!parsedPayload.upiId) {
      setScanError("QR scanned, but no valid UPI ID was found in the code.");
      setScanMessage("");
      return false;
    }

    setVerifiedUpi(parsedPayload);
    setScanError("");
    setScanMessage(
      parsedPayload.payeeName
        ? "QR scanned successfully. UPI ID, name, and amount were read from the QR."
        : "QR scanned successfully. UPI ID was read from the QR."
    );
    stopScanner();
    return true;
  };

  const handleScanQrClick = () => {
    setScanError("");
    setScanMessage("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleQrFileSelect = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const detector = detectorRef.current;
    if (!detector) {
      setScanError("QR image scanning is not supported in this browser. Please use Chrome or Edge.");
      return;
    }

    try {
      const imageBitmap = await createImageBitmap(file);
      const barcodes = await detector.detect(imageBitmap);
      const qrValue = barcodes[0]?.rawValue;

      if (qrValue && handleDetectedPayload(qrValue)) {
        return;
      }

      setScanError("No readable QR code was found in the selected image.");
      setScanMessage("");
    } catch (error) {
      console.error("Unable to scan QR image:", error);
      setScanError("QR image scanning failed. Please try a clearer QR screenshot.");
      setScanMessage("");
    }
  };

  const scanVideoFrame = async () => {
    const detector = detectorRef.current;
    const video = videoRef.current;

    if (!detector || !video || video.readyState < 2) {
      frameRef.current = requestAnimationFrame(scanVideoFrame);
      return;
    }

    try {
      const barcodes = await detector.detect(video);
      const qrValue = barcodes[0]?.rawValue;

      if (qrValue && handleDetectedPayload(qrValue)) {
        return;
      }
    } catch (error) {
      console.error("Error while scanning video frame:", error);
      setScanError("Camera opened, but QR scanning failed on this device.");
      stopScanner();
      return;
    }

    frameRef.current = requestAnimationFrame(scanVideoFrame);
  };

  const startScanner = async () => {
    setScanError("");
    setScanMessage("");
    setIsScannerOpen(true);

    if (!detectorRef.current) {
      setScanError("QR scanner is not supported in this browser. Please use Chrome or Edge.");
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setScanError("Camera access is not available on this device.");
      return;
    }

    try {
      stopScanner();
      setIsScannerOpen(true);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });

      streamRef.current = mediaStream;

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }

      frameRef.current = requestAnimationFrame(scanVideoFrame);
    } catch (error) {
      console.error("Unable to access camera:", error);
      setScanError("Camera permission was denied or the camera is unavailable.");
      stopScanner();
    }
  };


  const handleVerify = (event) => {
    event.preventDefault();
    setScanError("");
    setScanMessage("");
    const normalized = normalizeUpiId(form.upiId);
    const matchedRecord = records.find((record) => record.normalizedUpi === normalized);

    if (matchedRecord) {
      setVerifiedUpi({
        upiId: matchedRecord.upi || form.upiId,
        payeeName: matchedRecord.name || "",
        amount: form.amount,
      });
      setLookupMessage(`Matched from global source: ${matchedRecord.sourceCollection}`);
      return;
    }

    setVerifiedUpi({ upiId: form.upiId, payeeName: "", amount: ""});
    setLookupMessage("Checking live UPI verification...");
  };

  

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "upiId") {
      const normalized = normalizeUpiId(value);
      const matchedRecord = records.find((record) => record.normalizedUpi === normalized);

      setForm((prev) => ({
        ...prev,
        upiId: value,
        payeeName: matchedRecord?.name || "",
      }));

      if (!normalized) {
        setHasChecked(false);
        setLookupMessage(records.length ? `Loaded ${records.length} global UPI record(s).` : "No global UPI records found in the configured source.");
        return;
      }

      setHasChecked(true);
      setLookupMessage(
        matchedRecord
          ? `Matched from global source: ${matchedRecord.sourceCollection}`
          : "No matching UPI found in the global source."
      );
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!normalizedInput) {
      setHasChecked(false);
      setForm((prev) => (prev.payeeName ? { ...prev, payeeName: "" } : prev));
      setLiveLookupLoading(false);
      return;
    }

    setHasChecked(true);

    if (verifiedRecord && form.payeeName !== verifiedRecord.name) {
      setForm((prev) => ({ ...prev, payeeName: verifiedRecord.name || "" }));
      setLookupMessage(`Matched from global source: ${verifiedRecord.sourceCollection}`);
      setLiveLookupLoading(false);
      return;
    }

    if (!isValidUpiId(form.upiId)) {
      if (form.payeeName !== "") {
        setForm((prev) => ({ ...prev, payeeName: "" }));
      }
      setLiveLookupLoading(false);
      return;
    }

    let isCancelled = false;
    const timeoutId = setTimeout(async () => {
      setLiveLookupLoading(true);
      try {
        const { response, payload } = await fetchLiveUpiName(normalizedInput);

        if (isCancelled) return;

        if (response.ok && payload?.ok && payload?.name) {
          setForm((prev) => ({
            ...prev,
            payeeName: payload.name,
          }));
          setLookupMessage("Matched from live UPI verification.");
          return;
        }

        setForm((prev) => (prev.payeeName ? { ...prev, payeeName: "" } : prev));
        setLookupMessage(
          payload?.message || "No matching UPI found in the global source."
        );
      } catch (error) {
        if (isCancelled) return;
        setForm((prev) => (prev.payeeName ? { ...prev, payeeName: "" } : prev));
        setLookupMessage("Unable to verify UPI name live right now.");
      } finally {
        if (!isCancelled) {
          setLiveLookupLoading(false);
        }
      }
    }, 350);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [normalizedInput, verifiedRecord, form.upiId, form.payeeName]);

  const handleShare = async () => {
    if (!qrPayload || !shareImageRef.current) {
      setShareMessage("Enter a valid UPI ID first.");
      return;
    }

    try {
      const canvas = await html2canvas(shareImageRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));

      if (!blob) {
        throw new Error("Unable to create image for sharing");
      }

      const shareText = [
        `${form.payeeName || "Saved Name"}`,
        `Amount: Rs. ${form.amount.trim() || "Open Amount"}`,
        `UPI ID: ${form.upiId}`,
        "Scan the screenshot to pay.",
      ].join("\n");

      const file = new File([blob], `${form.payeeName || form.upiId || "upi-share"}.png`, {
        type: "image/png",
      });

      if (navigator.share) {
        try {
          await navigator.share({
            title: `${form.payeeName} Payment`,
            text: shareText,
            files: [file],
          });
          setShareMessage("Share sheet opened with image and payment details.");
          return;
        } catch (shareError) {
          if (shareError?.name === "AbortError") {
            setShareMessage("Share was canceled.");
            return;
          }
          console.error("Direct image share failed:", shareError);
        }
      }

      setShareMessage("WhatsApp image share is not supported on this phone/browser page.");
    } catch (error) {
      console.error("Unable to share payment details:", error);
      if (error?.name === "AbortError") {
        setShareMessage("Share was canceled.");
        return;
      }
      setShareMessage("Image sharing failed on this page. Try again from mobile Chrome using the secure URL.");
    }
  };

  const handleDownload = async () => {
    if (!qrPayload || !shareImageRef.current) {
      setShareMessage("Enter a valid UPI ID first.");
      return;
    }

    try {
      const canvas = await html2canvas(shareImageRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      });

      const imageUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = `${(form.payeeName || form.upiId || "upi-share").replace(/\s+/g, "-").toLowerCase()}.png`;
      link.click();
      setShareMessage("Image downloaded successfully.");
    } catch (error) {
      console.error("Unable to download payment image:", error);
      setShareMessage("Download failed. Please try again.");
    }
  };

 

  const showValidationError = hasChecked && form.upiId && !isValidUpiId(form.upiId);
  const amountText = form.amount.trim() ? `Rs. ${form.amount}` : "Open Amount";

  return (
    <div
      className="container-fluid py-4 px-2 px-md-3"
      style={{
        background:
          "radial-gradient(circle at top left, rgba(34,197,94,0.08), transparent 28%), radial-gradient(circle at top right, rgba(59,130,246,0.08), transparent 22%), #f6fbff",
        minHeight: "100vh",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >

        <div className="row g-4">
          <div className="col-12 col-xl-4">
            <div
              className="rounded-5 p-4 h-100"
              style={{
                background: "rgba(255,255,255,0.86)",
                border: "1px solid rgba(148,163,184,0.2)",
                boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
              }}
            >



              <form onSubmit={handleVerify}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleQrFileSelect}
                  style={{ display: "none" }}
                />
                <div className="mb-3">
                  <label className="form-label fw-bold fs-5">UPI ID</label>
                  <input
                    type="text"
                    name="upiId"
                    className="form-control form-control-lg"
                    value={form.upiId}
                    onChange={handleChange}

                    style={{ borderRadius: 16, minHeight: 56 }}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold fs-5">Name</label>
                  <input
                    type="text"
                    name="payeeName"
                    className="form-control form-control-lg"
                    value={form.payeeName}
                    onChange={handleChange}

                    style={{ borderRadius: 16, minHeight: 56 }}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold fs-5">Amount</label>
                  <input
                    type="text"
                    name="amount"
                    className="form-control form-control-lg"
                    value={form.amount}
                    onChange={handleChange}

                    style={{ borderRadius: 16, minHeight: 56 }}
                  />
                </div>

                <div className="d-flex gap-3 flex-wrap">
                  <button
                    type="submit"
                    className="btn btn-success"
                    style={{ borderRadius: 14, minWidth: 140, minHeight: 48 }}
                  >
                    Verify UPI
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-success"
                    onClick={handleScanQrClick}
                    style={{ borderRadius: 14, minWidth: 140, minHeight: 48 }}
                  >
                    Scan QR
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-dark"
                    onClick={startScanner}
                    style={{ borderRadius: 14, minWidth: 140, minHeight: 48 }}
                  >
                    Open Camera
                  </button>
                </div>

                {lookupMessage ? (
                  <div
                    className="mt-3 small"
                    style={{ color: verifiedRecord ? "#15803d" : "#b45309" }}
                  >
                    {lookupMessage}
                  </div>
                ) : null}

                {liveLookupLoading ? (
                  <div className="mt-2 small text-muted">Checking account name...</div>
                ) : null}

                {!verifiedRecord && isValidUpiId(form.upiId) ? (
                  <div className="mt-2 small text-muted">
                    QR can still be generated from the UPI ID even if no saved name is found.
                  </div>
                ) : null}
              </form>

              {isScannerOpen && (
                <div className="mt-4">
                  <div className="rounded-4 overflow-hidden position-relative" style={{ minHeight: 240, background: "#0f172a" }}>
                    <video
                      ref={videoRef}
                      muted
                      playsInline
                      className="w-100 h-100"
                      style={{ objectFit: "cover", minHeight: 240 }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        inset: "18% 18%",
                        border: "2px solid rgba(56,239,125,0.95)",
                        borderRadius: 18,
                        boxShadow: "0 0 0 9999px rgba(15,23,42,0.36)",
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-dark mt-3"
                    onClick={stopScanner}
                    style={{ borderRadius: 12 }}
                  >
                    Stop Camera
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="col-12 col-xl-8">
            <div
              ref={shareCardRef}
              className="rounded-5 p-3 p-md-4 h-100"
              style={{
                backgroundImage: `linear-gradient(135deg, rgba(26,34,64,0.96), rgba(26,95,105,0.78) 72%, rgba(31,141,126,0.8)), url(${profileHighlight})`,
                backgroundSize: "cover",
                backgroundPosition: "76% 18%",
                color: "white",
                boxShadow: "0 20px 44px rgba(21, 46, 84, 0.18)",
                overflow: "hidden",
              }}
            >
              <div
                className="rounded-5 p-4 h-100"
                style={{
                  border: "1px solid rgba(255,255,255,0.16)",
                  background:
                    "linear-gradient(135deg, rgba(15,23,42,0.28), rgba(255,255,255,0.02))",
                  backdropFilter: "blur(1.5px)",
                }}
              >
                <div className="text-center mb-4">
                  <div
                    className="d-inline-flex align-items-center rounded-pill px-4 py-2 mb-4"
                    style={{ background: "rgba(255,255,255,0.15)", fontWeight: 700 }}
                  >
                    SCAN & PAY
                  </div>
                  <div className="fw-bold mb-1" style={{ fontSize: "2rem" }}>
                    {form.payeeName || "Saved Name"}
                  </div>
                  <div className="text-white-50 mb-4 fs-5">{form.upiId || "verified-upi@bank"}</div>
                  <div className="text-uppercase text-white-50 fw-bold">Amount</div>
                  <div className="fw-bold" style={{ fontSize: "3rem", lineHeight: 1.1 }}>
                    {amountText}
                  </div>
                </div>

                <div
                  className="rounded-5 p-4 p-md-5 mb-4"
                  style={{
                    background: "rgba(255,255,255,0.18)",
                    minHeight: 340,
                    backdropFilter: "blur(6px)",
                    border: "1px solid rgba(255,255,255,0.24)",
                  }}
                >
                  <div className="row g-4 align-items-center h-100">
                    <div className="col-12 col-lg-7">
                      <div
                        ref={shareImageRef}
                        className="position-relative rounded-5 overflow-hidden h-100"
                        style={{
                          minHeight: 360,
                          aspectRatio: "1.42 / 1",
                          backgroundImage: `linear-gradient(135deg, rgba(15,23,42,0.1), rgba(15,23,42,0.02)), url(${profileHighlight})`,
                          backgroundSize: "cover",
                          backgroundPosition: "68% 14%",
                          border: "1px solid rgba(255,255,255,0.35)",
                          boxShadow: "0 18px 40px rgba(15,23,42,0.14)",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background:
                              "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.12))",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background:
                              "linear-gradient(180deg, rgba(0,0,0,0) 52%, rgba(15,23,42,0.18) 72%, rgba(15,23,42,0.42) 100%)",
                          }}
                        />
                        {qrImageUrl && (
                          <div
                            className="position-absolute"
                            style={{
                              left: "5.5%",
                              top: "6.5%",
                              background: "rgba(255,255,255,0.96)",
                              borderRadius: 20,
                              padding: 8,
                              boxShadow: "0 16px 36px rgba(15,23,42,0.18)",
                              border: "1px solid rgba(255,255,255,0.88)",
                            }}
                          >
                            <img
                              src={qrImageUrl}
                              alt="UPI QR"
                              className="img-fluid"
                              style={{
                                width: "clamp(118px, 31vw, 170px)",
                                height: "clamp(118px, 31vw, 170px)",
                                background: "#ffffff",
                                borderRadius: 12,
                                display: "block",
                              }}
                            />
                          </div>
                        )}
                        <div
                          className="position-absolute"
                          style={{
                            left: "6%",
                            bottom: "5.5%",
                            maxWidth: "52%",
                          }}
                        >
                          <div
                            className="fw-bold mb-3"
                            style={{
                              fontSize: "clamp(0.9rem, 3.2vw, 1.2rem)",
                              lineHeight: 1.15,
                              color: "#fff176",
                              fontWeight: 800,
                              textShadow: "0 4px 16px rgba(0,0,0,0.75)",
                              wordBreak: "break-word",
                            }}
                          >
                            {form.payeeName || "Saved Name"}
                          </div>
                           
                          <div
                            className="fw-bold"
                            style={{
                              fontSize: "clamp(1rem, 4vw, 1.5rem)",
                              lineHeight: 1.1,
                              color: "#fff176",
                              fontWeight: 800,
                              textShadow: "0 4px 16px rgba(0,0,0,0.75)",
                            }}
                          >
                            {form.amount.trim() ? `Rs. ${form.amount}` : "Open Amount"}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-lg-5 text-center">
                      {qrImageUrl ? (
                        <>
                          <div
                            className="rounded-4 p-4 mb-4"
                            style={{
                              background: "rgba(248,250,252,0.96)",
                              color: "#1e293b",
                              border: "1px solid rgba(255,255,255,0.65)",
                            }}
                          >
                            <div className="small text-uppercase text-muted fw-bold mb-2">Payment Details</div>
                            <div className="mb-3">
                              <div className="small text-muted">Name</div>
                              <div className="fw-bold fs-3">{form.payeeName || "Saved Name"}</div>
                            </div>
                            <div>
                              <div className="small text-muted">Amount</div>
                              <div className="fw-bold fs-3">
                                {form.amount.trim() ? `Rs. ${form.amount}` : "Open Amount"}
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-success btn-lg"
                            onClick={handleShare}
                            disabled={!qrPayload || !form.payeeName.trim()}
                            style={{ borderRadius: 16, minWidth: 170 }}
                          >
                            Share WhatsApp
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-light btn-lg ms-0 ms-md-3 mt-3 mt-md-0"
                            onClick={handleDownload}
                            disabled={!qrPayload || !form.payeeName.trim()}
                            style={{ borderRadius: 16, minWidth: 170 }}
                          >
                            Download Image
                          </button>
                        </>
                      ) : (
                        <div className="text-muted px-3">
                          Enter a valid UPI ID to auto-generate the QR preview.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {scanMessage && <div className="alert alert-info rounded-4 mt-4 mb-0">{scanMessage}</div>}
        {scanError && <div className="alert alert-danger rounded-4 mt-4 mb-0">{scanError}</div>}
        {shareMessage && <div className="alert alert-success rounded-4 mt-4 mb-0">{shareMessage}</div>}
        {showValidationError && (
          <div className="alert alert-warning rounded-4 mt-4 mb-0">
            Please enter a valid UPI ID format like <strong>name@bank</strong>.
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default UpiVerify;
